import { createAsyncThunk } from "@reduxjs/toolkit";
import apiInstance from "../../config/api";
import { updateBlockStatus } from "../chat/chatReducer";
import { readNotification } from "../notification/notificationAction";

// Helper function to handle API errors properly
const handleApiError = (error) => {
  console.error('API Error:', error);
  
  // If we have a response from the server, use that message
  if (error.response && error.response.data) {
    const serverError = error.response.data;
    throw new Error(serverError.message || serverError.error || 'Server error occurred');
  }
  
  // If it's a network error or no response
  if (error.message === 'Network Error') {
    throw new Error('Network error: Please check your internet connection');
  }
  
  // Fallback to the original error message
  throw new Error(error.message || 'An unexpected error occurred');
};

export const getPendingFriendRequests = createAsyncThunk(
  "friend-request/pending-request",
  async (userId) => {
    try {
      const request = await apiInstance.get(`/api/friends/pending-requests/${userId}`);
      const response = request.data;
      
      if (response.status === "success") {
        const senderArray = response.data.map((item) => item.user);
        return senderArray;
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      throw handleApiError(error);
    }
  }
);

export const sendFriendRequest = createAsyncThunk(
  "friend-request/send",
  async ({ userId, friendId }) => {
    try {
      const request = await apiInstance.post(`/api/friends/send-request/${userId}/${friendId}`);
      const response = request.data;
      
      if (response.status === "success") {
        return response.message;
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      throw handleApiError(error);
    }
  }
);

export const cancelFriendRequest = createAsyncThunk(
  "friend-request/cancel",
  async ({ userId, friendId }) => {
    try {
      const request = await apiInstance.delete(`/api/friends/unsend-request/${userId}/${friendId}`);
      const response = request.data;
      
      if (response.status === "success") {
        return response.message;
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      throw handleApiError(error);
    }
  }
);

export const acceptFriendRequest = createAsyncThunk(
  "friend-request/accept",
  async ({ userId, friendId }, { rejectWithValue, dispatch, getState }) => {
    try {
      console.log('Accepting friend request:', { userId, friendId });

      const request = await apiInstance.post(`/api/friends/accept-request/${userId}/${friendId}`);
      const response = request.data;

      console.log('Accept response:', response);

      if (response.status === "success") {
        // The pending "friend request" notification from this user is now stale -
        // mark it read so it stops showing as an outstanding invite.
        try {
          const { notifications } = getState().notifications;
          const staleNotification = notifications?.find((notification) => {
            const type = notification?.payload?.data?.type || notification?.type;
            const senderId = notification?.payload?.data?.user?.userId;
            return type === "FriendRequest" && senderId === friendId && !notification.read;
          });
          if (staleNotification) {
            dispatch(readNotification(staleNotification.id));
          }
        } catch (notificationError) {
          console.log('unable to clear friend request notification: ', notificationError.message);
        }

        return { message: response.message, friendId };
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Error in acceptFriendRequest:', error);
      
      // Use rejectWithValue to preserve the error details
      if (error.response && error.response.data) {
        return rejectWithValue({
          message: error.response.data.message || error.response.data.error,
          status: error.response.status,
          data: error.response.data
        });
      }
      
      return rejectWithValue({
        message: error.message || 'Failed to accept friend request',
        status: 0
      });
    }
  }
);

export const declineFriendRequest = createAsyncThunk(
  "friend-request/decline",
  async ({ userId, friendId }, { rejectWithValue }) => {
    try {
      console.log('Declining friend request:', { userId, friendId });
      
      const request = await apiInstance.post(`/api/friends/decline-request/${userId}/${friendId}`);
      const response = request.data;
      
      console.log('Decline response:', response);
      
      if (response.status === "success") {
        return { message: response.message, friendId };
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Error in declineFriendRequest:', error);
      
      if (error.response && error.response.data) {
        return rejectWithValue({
          message: error.response.data.message || error.response.data.error,
          status: error.response.status,
          data: error.response.data
        });
      }
      
      return rejectWithValue({
        message: error.message || 'Failed to decline friend request',
        status: 0
      });
    }
  }
);

export const unfriendUser = createAsyncThunk(
  "unfriends",
  async ({ userId, friendId }, { rejectWithValue }) => {
    try {
      console.log('Unfriending user:', { userId, friendId });
      
      const request = await apiInstance.delete(`/api/friends/unfriend/${userId}/${friendId}`);
      const response = request.data;
      
      console.log('Unfriend response:', response);
      
      if (response.status === "success") {
        return { message: response.message, friendId };
      } else {
        throw new Error(response.message);
      }
    } catch (error) {
      console.error('Error in unfriendUser:', error);
      
      if (error.response && error.response.data) {
        return rejectWithValue({
          message: error.response.data.message || error.response.data.error,
          status: error.response.status,
          data: error.response.data
        });
      }
      
      return rejectWithValue({
        message: error.message || 'Failed to unfriend user',
        status: 0
      });
    }
  }
);

export const blockUser = createAsyncThunk('user/block', async (userId, { rejectWithValue, dispatch }) => {
  try {
    const request = await apiInstance.post(`/api/block/${userId}`)
    const response = await request.data
    
    if (response.status === 'success') {
      dispatch(updateBlockStatus({ userId, youBlockedThem: true }))
      return userId
    } else {
      throw new Error(response.message)
    }
  }
  catch (error) {
    if (error.response && error.response.data) {
      return rejectWithValue({
        message: error.response.data.message || error.response.data.error,
        status: error.response.status
      });
    }
    
    return rejectWithValue({
      message: error.message || 'Failed to block user',
      status: 0
    });
  }
})

export const unblockUser = createAsyncThunk('user/unblock', async (userId, { rejectWithValue, dispatch }) => {
  try {
    const request = await apiInstance.delete(`/api/unblock/${userId}`)
    const response = await request.data
    
    if (response.status === 'success') {
      dispatch(updateBlockStatus({ userId, youBlockedThem: false }))
      return userId
    } else {
      throw new Error(response.message)
    }
  }
  catch (error) {
    if (error.response && error.response.data) {
      return rejectWithValue({
        message: error.response.data.message || error.response.data.error,
        status: error.response.status
      });
    }
    
    return rejectWithValue({
      message: error.message || 'Failed to unblock user',
      status: 0
    });
  }
})

export const getBlockUsers = createAsyncThunk('users/block/get', async (_, { rejectWithValue }) => {
  try {
    const request = await apiInstance.get(`/api/showAllBlocks`)
    const response = await request.data
    
    if (response.status === 'success') {
      const blockUsers = [...response.blockedUsers].map(user => ({
        ...user.userDetails
      }))
      return blockUsers
    } else {
      throw new Error(response.message)
    }
  }
  catch (error) {
    if (error.response && error.response.data) {
      return rejectWithValue({
        message: error.response.data.message || error.response.data.error,
        status: error.response.status
      });
    }
    
    return rejectWithValue({
      message: error.message || 'Failed to get blocked users',
      status: 0
    });
  }
})

export const getSuggestedFriends = createAsyncThunk('friend/suggest', async (_, { rejectWithValue }) => {
  try {
    const request = await apiInstance.get('/api/friends/suggest/people')
    const response = request.data
    
    if (response.status === 'success') {
      const users = response.potentialFriends
      const mappedSearchedResult = users.map(user => ({
        firstName: user.firstName,
        lastName: user.lastName,
        profilePhoto: user.profilePhoto,
        userId: user.userId,
        userName: user.UserName,
        friendshipStatus: user.friendshipStatus?.message
      }))
      
      return mappedSearchedResult
    } else {
      throw new Error(response.message)
    }
  }
  catch (error) {
    if (error.response && error.response.data) {
      return rejectWithValue({
        message: error.response.data.message || error.response.data.error,
        status: error.response.status
      });
    }
    
    return rejectWithValue({
      message: error.message || 'Failed to get suggested friends',
      status: 0
    });
  }
})